import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { calendars, projects, events, milestones } from '../api/index.js';
import { useOfflineCache } from './useOfflineCache.js';
import { toDateString } from '../utils/dateUtils.js';

/**
 * Remote data source hook - syncs with backend API
 * @param {string} calendarId - Calendar ID to load
 * @returns {Object} Data source interface matching useLocalDataSource
 */
export function useRemoteDataSource(calendarId) {
  const [projectList, setProjectList] = useState([]);
  const [eventList, setEventList] = useState([]);
  const [milestoneList, setMilestoneList] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // WebSocket connection
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const wsConnectedRef = useRef(false);
  const wsErrorCountRef = useRef(0);
  
  // Offline cache
  const { cachedData, isOffline, updateCache } = useOfflineCache(calendarId);

  // Load initial data
  useEffect(() => {
    let mounted = true;

    async function loadData() {
      if (!calendarId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const data = await calendars.get(calendarId);
        
        if (mounted) {
          // Sort projects by sortOrder
          const sortedProjects = [...data.projects].sort((a, b) => a.sortOrder - b.sortOrder);
          setProjectList(sortedProjects);
          setEventList(data.events);
          setMilestoneList(data.milestones);
          
          // Select first non-system project or null
          const firstProject = sortedProjects.find(p => !p.isSystem);
          setSelectedProjectId(firstProject?.id || null);
          
          // Cache for offline use
          updateCache({
            projects: sortedProjects,
            events: data.events,
            milestones: data.milestones,
          });
        }
      } catch (err) {
        if (mounted) {
          // Use cached data on ANY error (not just when navigator.onLine is false)
          // This handles both offline scenarios and backend failures (500 errors, etc.)
          if (cachedData) {
            setProjectList(cachedData.projects || []);
            setEventList(cachedData.events || []);
            setMilestoneList(cachedData.milestones || []);
            const firstProject = cachedData.projects?.find(p => !p.isSystem);
            setSelectedProjectId(firstProject?.id || null);
            // Set error to indicate we're in offline/degraded mode
            setError('offline');
          } else {
            // No cached data available, show the actual error
            setError(err.message);
          }
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    loadData();

    return () => {
      mounted = false;
    };
    // cachedData and updateCache intentionally omitted to avoid infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarId]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!calendarId) return;

    // Handle WebSocket messages - defined inside effect to capture calendarId
    // Messages can come from:
    // 1. Our own calendar (message.calendarId === calendarId)
    // 2. Included/shared projects (message.projectId is in our project list)
    function handleWSMessage(message) {
      // For project messages, check if it's our calendar
      // For event/milestone messages, check if the project is in our list
      const isOurCalendar = message.calendarId === calendarId;
      
      switch (message.type) {
        case 'project':
          // Project changes only from our own calendar
          if (!isOurCalendar) return;
          
          if (message.action === 'create') {
            setProjectList(prev => {
              // Check if project already exists to prevent duplicates
              if (prev.some(p => p.id === message.data.id)) {
                return prev;
              }
              return [...prev, message.data].sort((a, b) => a.sortOrder - b.sortOrder);
            });
          } else if (message.action === 'update') {
            setProjectList(prev => prev.map(p => p.id === message.data.id ? message.data : p));
          } else if (message.action === 'delete') {
            setProjectList(prev => prev.filter(p => p.id !== message.data.id));
          } else if (message.action === 'reorder') {
            setProjectList(prev => {
              const { orderedIds } = message.data;
              return [...prev].sort((a, b) => orderedIds.indexOf(a.id) - orderedIds.indexOf(b.id));
            });
          }
          break;

        case 'event':
          // Event changes - check if we have this project (owned or included)
          setProjectList(currentProjects => {
            const hasProject = currentProjects.some(p => p.id === message.projectId);
            if (!hasProject) return currentProjects;
            
            // Update event list based on action
            if (message.action === 'create') {
              setEventList(prev => {
                // Check if event already exists to prevent duplicates
                if (prev.some(e => e.id === message.data.id)) {
                  return prev;
                }
                return [...prev, message.data];
              });
            } else if (message.action === 'update') {
              setEventList(prev => prev.map(e => e.id === message.data.id ? message.data : e));
            } else if (message.action === 'delete') {
              setEventList(prev => prev.filter(e => e.id !== message.data.id));
            }
            
            return currentProjects; // Return unchanged
          });
          break;

        case 'milestone':
          // Milestone changes - check if we have this project (owned or included)
          setProjectList(currentProjects => {
            const hasProject = currentProjects.some(p => p.id === message.projectId);
            if (!hasProject) return currentProjects;
            
            // Update milestone list based on action
            if (message.action === 'create') {
              setMilestoneList(prev => {
                // Check if milestone already exists to prevent duplicates
                if (prev.some(m => m.id === message.data.id)) {
                  return prev;
                }
                return [...prev, message.data];
              });
            } else if (message.action === 'update') {
              setMilestoneList(prev => prev.map(m => m.id === message.data.id ? message.data : m));
            } else if (message.action === 'delete') {
              setMilestoneList(prev => prev.filter(m => m.id !== message.data.id));
            }
            
            return currentProjects; // Return unchanged
          });
          break;

        case 'calendar':
          if (!isOurCalendar) return;
          
          if (message.action === 'refresh') {
            // Reload all data
            calendars.get(calendarId).then(data => {
              const sortedProjects = [...data.projects].sort((a, b) => a.sortOrder - b.sortOrder);
              setProjectList(sortedProjects);
              setEventList(data.events);
              setMilestoneList(data.milestones);
            });
          }
          break;

        case 'share':
          // Handle share notifications (granted/revoked)
          if (message.action === 'revoked') {
            const revokedProjectId = message.data?.projectId;
            if (revokedProjectId) {
              // Remove the project from our list if it was included
              setProjectList(prev => prev.filter(p => p.id !== revokedProjectId));
              // Remove events and milestones for that project
              setEventList(prev => prev.filter(e => e.projectId !== revokedProjectId));
              setMilestoneList(prev => prev.filter(m => m.projectId !== revokedProjectId));
            }
          }
          // Dispatch custom event so Header can update pending shares count
          window.dispatchEvent(new CustomEvent('lincal:share-changed', { 
            detail: { action: message.action, data: message.data }
          }));
          break;
      }
    }

    function connect() {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(`${protocol}//${window.location.host}/ws`);
      wsRef.current = ws;

      ws.onopen = () => {
        // Subscribe to calendar updates
        ws.send(JSON.stringify({ type: 'subscribe', calendarId }));
        wsConnectedRef.current = true;
        wsErrorCountRef.current = 0;
        // Clear offline error if we were previously disconnected
        setError(null);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleWSMessage(message);
        } catch (err) {
          console.error('WebSocket message error:', err);
        }
      };

      ws.onclose = () => {
        wsConnectedRef.current = false;
        wsErrorCountRef.current++;
        
        // If we've had multiple connection failures, set offline error
        // Wait for a few failures to avoid flickering on temporary disconnects
        if (wsErrorCountRef.current >= 2) {
          setError('offline');
        }
        
        // Reconnect with exponential backoff: 1s, 2s, 4s, 8s, 16s, max 30s
        const reconnectDelay = Math.min(1000 * Math.pow(2, wsErrorCountRef.current - 1), 30000);
        reconnectTimeoutRef.current = setTimeout(connect, reconnectDelay);
      };

      ws.onerror = (err) => {
        console.error('WebSocket error:', err);
        wsConnectedRef.current = false;
        ws.close();
      };
    }

    connect();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [calendarId]);

  // Project operations
  const addProject = useCallback(async (name, color) => {
    const project = await projects.create(calendarId, { name, color });
    setProjectList(prev => {
      // Prevent duplicate if WebSocket already added it
      if (prev.some(p => p.id === project.id)) {
        return prev;
      }
      return [...prev, project].sort((a, b) => a.sortOrder - b.sortOrder);
    });
    return project;
  }, [calendarId]);

  const updateProject = useCallback(async (id, updates, localOnly = false) => {
    if (localOnly) {
      // For included projects, only update local state (API call already made)
      setProjectList(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
    } else {
      const project = await projects.update(calendarId, id, updates);
      setProjectList(prev => prev.map(p => p.id === id ? project : p));
    }
  }, [calendarId]);

  const deleteProject = useCallback(async (id) => {
    await projects.delete(calendarId, id);
    setProjectList(prev => prev.filter(p => p.id !== id));
    // Also remove events and milestones for this project
    setEventList(prev => prev.filter(e => e.projectId !== id));
    setMilestoneList(prev => prev.filter(m => m.projectId !== id));
  }, [calendarId]);

  const toggleProjectVisibility = useCallback(async (id) => {
    const project = projectList.find(p => p.id === id);
    if (project) {
      if (project.isIncluded) {
        // For included projects, update via shares API
        const { shares } = await import('../api/index.js');
        await shares.updateIncluded(project.includedId, { visible: !project.visible });
        // Update local state
        setProjectList(prev => prev.map(p => p.id === id ? { ...p, visible: !p.visible } : p));
      } else {
        await updateProject(id, { visible: !project.visible });
      }
    }
  }, [projectList, updateProject]);

  const reorderProjects = useCallback(async (orderedIds) => {
    await projects.reorder(calendarId, orderedIds);
    setProjectList(prev => {
      return [...prev].sort((a, b) => orderedIds.indexOf(a.id) - orderedIds.indexOf(b.id));
    });
  }, [calendarId]);

  const selectProject = useCallback((id) => {
    setSelectedProjectId(id);
  }, []);

  // Event operations
  const addEvent = useCallback(async (title, startDate, endDate, projectId, description) => {
    const event = await events.create(calendarId, {
      title,
      startDate: toDateString(startDate),
      endDate: toDateString(endDate),
      projectId,
      description: description || null,
    });
    setEventList(prev => [...prev, event]);
    return event;
  }, [calendarId]);

  const updateEvent = useCallback(async (id, updates) => {
    // Convert Date objects to YYYY-MM-DD strings
    const normalizedUpdates = { ...updates };
    if (updates.startDate) normalizedUpdates.startDate = toDateString(updates.startDate);
    if (updates.endDate) normalizedUpdates.endDate = toDateString(updates.endDate);
    
    // Optimistic update: apply changes immediately
    let previousEvent = null;
    setEventList(prev => prev.map(e => {
      if (e.id === id) {
        previousEvent = e;
        return { ...e, ...normalizedUpdates };
      }
      return e;
    }));
    
    try {
      // Update on server
      const event = await events.update(calendarId, id, normalizedUpdates);
      // Apply server response (in case server modified the data)
      setEventList(prev => prev.map(e => e.id === id ? event : e));
    } catch (error) {
      // Rollback on error
      if (previousEvent) {
        setEventList(prev => prev.map(e => e.id === id ? previousEvent : e));
      }
      throw error;
    }
  }, [calendarId]);

  const deleteEvent = useCallback(async (id) => {
    await events.delete(calendarId, id);
    setEventList(prev => prev.filter(e => e.id !== id));
  }, [calendarId]);

  // Milestone operations
  const addMilestone = useCallback(async (title, date, icon, projectId, color, description) => {
    const milestone = await milestones.create(calendarId, {
      title,
      date: toDateString(date),
      icon: icon || 'flag',
      projectId,
      color: color || null,
      description: description || null,
    });
    setMilestoneList(prev => [...prev, milestone]);
    return milestone;
  }, [calendarId]);

  const updateMilestone = useCallback(async (id, updates) => {
    // Convert Date objects to YYYY-MM-DD strings
    const normalizedUpdates = { ...updates };
    if (updates.date) normalizedUpdates.date = toDateString(updates.date);
    
    // Optimistic update: apply changes immediately
    let previousMilestone = null;
    setMilestoneList(prev => prev.map(m => {
      if (m.id === id) {
        previousMilestone = m;
        return { ...m, ...normalizedUpdates };
      }
      return m;
    }));
    
    try {
      // Update on server
      const milestone = await milestones.update(calendarId, id, normalizedUpdates);
      // Apply server response (in case server modified the data)
      setMilestoneList(prev => prev.map(m => m.id === id ? milestone : m));
    } catch (error) {
      // Rollback on error
      if (previousMilestone) {
        setMilestoneList(prev => prev.map(m => m.id === id ? previousMilestone : m));
      }
      throw error;
    }
  }, [calendarId]);

  const deleteMilestone = useCallback(async (id) => {
    await milestones.delete(calendarId, id);
    setMilestoneList(prev => prev.filter(m => m.id !== id));
  }, [calendarId]);

  // Holiday operations (holidays are events in the system Holidays project)
  const holidaysProject = projectList.find(p => p.isSystem);
  const holidayProjectId = holidaysProject?.id;

  const holidays = eventList.filter(e => e.projectId === holidayProjectId);

  const addHoliday = useCallback(async (title, startDate, endDate, description) => {
    if (!holidayProjectId) {
      throw new Error('Holidays project not found');
    }
    return addEvent(title, startDate, endDate, holidayProjectId, null, description);
  }, [addEvent, holidayProjectId]);

  const updateHoliday = useCallback(async (id, updates) => {
    return updateEvent(id, updates);
  }, [updateEvent]);

  const deleteHoliday = useCallback(async (id) => {
    return deleteEvent(id);
  }, [deleteEvent]);

  // Import/Export
  const importData = useCallback(async (data) => {
    await calendars.import(calendarId, data);
    // Reload data after import
    const newData = await calendars.get(calendarId);
    const sortedProjects = [...newData.projects].sort((a, b) => a.sortOrder - b.sortOrder);
    setProjectList(sortedProjects);
    setEventList(newData.events);
    setMilestoneList(newData.milestones);
  }, [calendarId]);

  const resetData = useCallback(async () => {
    // Import empty data to reset
    await importData({
      version: 2,
      projects: [],
      events: [],
      milestones: [],
    });
  }, [importData]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Filter out system projects (holidays) from regular projects list
  const regularProjects = useMemo(() => 
    projectList.filter(p => !p.isSystem),
    [projectList]
  );

  return useMemo(() => {
    // Build data object for export inside useMemo to avoid recreating on every render
    const data = {
      version: 2,
      projects: projectList,
      events: eventList,
      milestones: milestoneList,
      selectedProjectId,
    };

    return {
      // Data
      projects: regularProjects,
      events: eventList,
      milestones: milestoneList,
      holidays,
      selectedProjectId,
      
      // Project operations
      addProject,
      updateProject,
      deleteProject,
      toggleProjectVisibility,
      reorderProjects,
      selectProject,
      
      // Event operations
      addEvent,
      updateEvent,
      deleteEvent,
      
      // Milestone operations
      addMilestone,
      updateMilestone,
      deleteMilestone,
      
      // Holiday operations
      addHoliday,
      updateHoliday,
      deleteHoliday,
      
      // Import/Export
      importData,
      resetData,
      data,
      
      // Loading state
      isLoading,
      error,
      clearError,
      
      // Offline state
      isOffline,
    };
  }, [
    regularProjects,
    projectList,
    eventList,
    milestoneList,
    holidays,
    selectedProjectId,
    addProject,
    updateProject,
    deleteProject,
    toggleProjectVisibility,
    reorderProjects,
    selectProject,
    addEvent,
    updateEvent,
    deleteEvent,
    addMilestone,
    updateMilestone,
    deleteMilestone,
    addHoliday,
    updateHoliday,
    deleteHoliday,
    importData,
    resetData,
    isLoading,
    error,
    clearError,
    isOffline,
  ]);
}

export default useRemoteDataSource;
